/**
 * Core Utilities for Kazakan Tools
 * Privacy-first, zero-dependency helper functions
 */

/**
 * Debounce function: delays execution until after wait milliseconds
 * @param {Function} func 
 * @param {number} wait 
 * @returns {Function}
 */
export function debounce(func, wait = 250) {
  let timeoutId = null;
  const debounced = function (...args) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
      timeoutId = null;
    }, wait);
  };
  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  return debounced;
}

/**
 * Throttle function: limits execution to at most once per limit milliseconds
 * @param {Function} func 
 * @param {number} limit 
 * @returns {Function}
 */
export function throttle(func, limit = 100) {
  let inThrottle = false;
  let lastArgs = null;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func.apply(this, lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
}

/**
 * Safely escape HTML characters to prevent XSS injection
 * @param {string} str 
 * @returns {string}
 */
export function escapeHTML(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Safely sets plain text content on an element (Strict XSS prevention)
 * @param {HTMLElement|string} target Element or selector
 * @param {string} text Plain text content
 */
export function setTextSafe(target, text) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (el) {
    el.textContent = text ?? '';
  }
}

/**
 * Normalizes user search query for cross-language Arabic and English matching
 * Handles Arabic diacritics, tatweel, hamza variants, taa marbuta, alif maqsura,
 * and normalizes whitespace and punctuation.
 * @param {string} str
 * @returns {string}
 */
export function normalizeSearchQuery(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/\u0640/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ئ/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/[—_,\-.:;!?/\\|()[\]{}'"`~*+^%$#@<>=]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
